import { StoreExt } from '@utils/reactExt'
import * as io from 'socket.io-client'
import {
  authStore,
  bettingHistoryStore,
  chattingStore,
  componentStore,
  globalStore,
  matchupStore,
  messageStore,
  userStore,
  popularSearchTermStore,
} from '@store/index'
import { PORTAL_SOCKET_EVENT, PushBetResultPacket } from '@protocol/socket'
import { showToast, TOAST_TYPE } from '@utils/utils'
import {
  LogoutType,
  Notice,
  Schema$GameHomeMainBanner,
  Schema$UpdateBettingTotalCount,
  Schema$UpdateLiveMatchCountTopic,
} from '@interface/common'
import { IMessageOne } from '@protocol/message'
import { SOCKET_EVENT } from '@constants/socket'
import { isMobile } from 'react-device-detect'
import { schema_urgency_message } from 'adv-lib/src/models/dedicated/urgency_message/urgency_message'
import { schema_global } from 'adv-lib/src/models/game/global'
import { UserActivityInfo } from '@interface/user-activity'
import { Schema$PopularSearchTerm } from '@protocol/popularSearchTerm'

// TODO  onHandler 등록을 편하게 할 수 있어야 한다.
export class PortalSocketStore extends StoreExt {
  socket: SocketIOClient.Socket

  constructor() {
    super()
    this.socket = io.connect(`${process.env.SOCKET_URL}`)
    console.log('==== socket connection', this.socket, `${process.env.SOCKET_URL}`)
    this.initPortalSocketOnHandler()
  }

  documentVisibleChangeHandler = (isHidden: boolean): void => {
    if (!isMobile) {
      return
    }

    if (isHidden) {
      this.socket.disconnect()
    } else {
      this.socket.connect()
    }
  }

  registerClient = () => {
    if (authStore.isSignedIn) {
      const userPromiseId = authStore.userInfo.id
      this.socket.emit(PORTAL_SOCKET_EVENT.REGISTER_CLIENT, { userPromiseId, isMobile: isMobile })
    }
  }

  initPortalSocketOnHandler = () => {
    this.socket.on(SOCKET_EVENT.CONNECTION, () => {
      console.log('==== socket connection success', this.socket)
      if (authStore.isSignedIn) {
        this.registerClient()
      }
    })

    // this.socket.on(PORTAL_SOCKET_ON_TYPE.UPDATE_USER_ASSETS, user => {
    //   authStore.updateUserAssets(user)
    // })

    this.socket.on(PORTAL_SOCKET_EVENT.DUPLICATE_LOGIN_EXCEPTION, async (): Promise<void> => {
      showToast(TOAST_TYPE.INFO, '중복 로그인으로 로그아웃 합니다.')
      await authStore.logout(LogoutType.DUPLICATE)
    })

    this.socket.on(PORTAL_SOCKET_EVENT.UPDATE_GLOBAL, (msg: schema_global): void => {
      const { key, value } = msg
      globalStore.syncGlobal(key, value)
      chattingStore.setChattingOptions()
      componentStore.updateIsVisibleEtcAlertOptions()
    })

    this.socket.on(PORTAL_SOCKET_EVENT.PUSH_BET_RESULT, (msg: PushBetResultPacket): void => {
      matchupStore.pushBetResult(msg).catch(console.error)
    })

    this.socket.on(PORTAL_SOCKET_EVENT.KICK, async (): Promise<void> => {
      await authStore.logout(LogoutType.KICK)
    })

    this.socket.on(PORTAL_SOCKET_EVENT.RECEIVE_CLUB_MESSAGE, (msg: IMessageOne.SocketData): void => {
      const clubId = msg.clubId
      const message = msg.messageObject
      if (matchupStore.club?.id === clubId) {
        messageStore.setupUnReadClubMessage(message, clubId)
      }
    })

    this.socket.on(PORTAL_SOCKET_EVENT.RECEIVE_USER_MESSAGE, (msg: IMessageOne.SocketDataByUser): void => {
      const { unreadMessageCount } = msg
      userStore.increaseUnreadMessageCount(unreadMessageCount)
      userStore.increaseUnQuickReadMessageCount(unreadMessageCount)
    })

    this.socket.on(PORTAL_SOCKET_EVENT.UPDATE_LIVE_MATCH_COUNT, (msg: Schema$UpdateLiveMatchCountTopic[]): void => {
      for (const el of msg) {
        if (matchupStore.club?.id === el.clubId) {
          userStore.setLiveMatchCount(el.liveMatchCount)
          break
        }
      }
    })

    this.socket.on(PORTAL_SOCKET_EVENT.UPDATE_BETTING_TOTAL_COUNT, (msg: Schema$UpdateBettingTotalCount): void => {
      if (msg) {
        setTimeout(() => {
          bettingHistoryStore.setOnGoingBettingCount(msg.totalCount)
        }, 500)
      }
    })

    this.socket.on(PORTAL_SOCKET_EVENT.UPDATE_USER_PLAY_BLOCK_TIME, async (): Promise<void> => {
      await authStore.refreshSimpleUserInfo(true)
    })

    this.socket.on(PORTAL_SOCKET_EVENT.RECEIVE_URGENCY_MESSAGE, (msg: schema_urgency_message): void => {
      const { title, content } = msg
      componentStore.setUrgencyMessage({ title, content })
      globalStore.setVisibleUrgencyMessagePopup(true)
    })

    this.socket.on(PORTAL_SOCKET_EVENT.UPDATE_MATCHUP_NOTICE, (msg: Notice): void => {
      if (authStore.isSignedIn) {
        componentStore.fetchMatchupNotices().catch(console.error)
      }
    })

    this.socket.on(PORTAL_SOCKET_EVENT.DELETE_MATCHUP_NOTICE, (msg: Notice): void => {
      componentStore.updateMatchupNotice(msg)
    })

    this.socket.on(PORTAL_SOCKET_EVENT.ON_REGISTER_MATCH_TIMELINE_WATCHING, (matchId: number) => {
      chattingStore.registerMatchTimelineWatchingListByBettingHistory(matchId)
    })

    this.socket.on(PORTAL_SOCKET_EVENT.ON_REALTIME_LIVE_SCORE_MATCH_IDS_BY_BETTING_HISTORY, (matchIds: number[]) => {
      chattingStore.onRealTimeLiveScoreMatchIdsByBettingHistory(matchIds)
    })

    this.socket.on(PORTAL_SOCKET_EVENT.UPDATE_MY_ACTIVITY_INFO, (userActivityInfo: UserActivityInfo) => {
      globalStore.setUserActivityInfo(userActivityInfo)
    })

    this.socket.on(PORTAL_SOCKET_EVENT.ON_SIMPLE_LIVE_MATCH_LIST, data => {
      chattingStore.onSimpleMatchTimelineGroupListHandler(data).catch(console.error)
    })

    this.socket.on(PORTAL_SOCKET_EVENT.UPDATE_POPULAR_SEARCH_TERM, (term: Schema$PopularSearchTerm): void => {
      popularSearchTermStore.updatePopularSearchTerm(term)
    })

    this.socket.on(PORTAL_SOCKET_EVENT.UPDATE_POPULAR_SEARCH_TERM_LIST, (terms: Schema$PopularSearchTerm[]): void => {
      popularSearchTermStore.updatePopularSearchTermList(terms)
    })

    this.socket.on(PORTAL_SOCKET_EVENT.DELETE_POPULAR_SEARCH_TERM, (id: number): void => {
      popularSearchTermStore.deletePopularSearchTerm(id)
    })

    this.socket.on(PORTAL_SOCKET_EVENT.DELETE_POPULAR_SEARCH_TERM_LIST, (termIds: number[]): void => {
      popularSearchTermStore.deletePopularSearchTermList(termIds)
    })

    this.socket.on(PORTAL_SOCKET_EVENT.UPDATE_HOME_MAIN_BANNER, (banner: Schema$GameHomeMainBanner): void => {
      componentStore.updateGameHomeMainBanner(banner)
    })

    this.socket.on(PORTAL_SOCKET_EVENT.DELETE_HOME_MAIN_BANNER, (id: string): void => {
      componentStore.deleteGameHomeMainBanner(id)
    })

    this.socket.on(PORTAL_SOCKET_EVENT.REFRESH_USER_PROFILE_IMAGE, (): void => {
      authStore.refreshSimpleUserInfo(false)
    })
  }
}

export default new PortalSocketStore()
